import React, { useState, useEffect } from "react";
import { Row, Col, Tag, Tooltip } from "antd";
import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  FileTextOutlined,
  WarningOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useGetAlertsSummaryQuery } from "../../../Redux/service/fsm/FSMDashboard";
import Loader from "../../Helpers/Loader/Loader";
import trans, { calculateDelayDuration } from "../../../utils";
import AlertPopup from "../FMSDashboardPopups/AlertPopup";
import dayjs from "dayjs";
import { formatDateString } from "../../../JsHelpers/DateFormat";

const colorScheme = {
  critical: "red",
  high: "volcano",
  medium: "orange",
  low: "green",
  neutral: "blue",
  success: "green",
  danger: "red",
};
const getSeverityColor = (severity) => {
  switch (severity) {
    case "critical":
      return "error";
    case "high":
      return "warning";
    case "medium":
      return "processing";
    case "low":
      return "success";
    default:
      return "default";
  }
};

export default function FSMAlertsSummary({ refreshKey, canViewServiceRequest = true }) {
  const { data: alerts, isLoading, refetch } = useGetAlertsSummaryQuery({ refreshKey }, { refetchOnMountOrArgChange: true });
  const [selectedAlert, setSelectedAlert] = useState(null);

  useEffect(() => {
    refetch();
}, []);

  if (isLoading) {
    return <Loader />;
  }

  const alertTypes = [
    {
      key: "delayedTasks",
      title: trans("Delayed Tasks"),
      icon: <ClockCircleOutlined />,
      color: colorScheme.critical,
      severity: "High",
      tooltip: trans("Delayed tasks beyond deadlines."),
      link: "tasks/tasks-board?isDelayed=true&filterBy=todo,unassigned",
      api: "useGetTaskAlertsQuery",
      query: {
        page: 1,
        limit: 10,
        isDelayed: true,
        filterBy: "todo,unassigned",
      },
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Task Name"), dataIndex: "name", width: 200 },
        {
          title: trans("Assigned Field Staff"),
          dataIndex: "assignedTo",
          width: 150,
          render: (value) => value?.name || "- - - - -",
        },
        {
          title: trans("Delay Duration"),
          dataIndex: "startTime",
          width: 120,
          render: (value) => calculateDelayDuration(value),
        },
        { title: trans("Priority"), dataIndex: "priority", width: 100 },
      ],
      extractTotal: (response) => response?.totalResults || 0,
    },
    {
      key: "slaViolations",
      title: trans("SLA Violations"),
      icon: <ExclamationCircleOutlined />,
      color: colorScheme.danger,
      severity: "Critical",
      tooltip: trans("SLA violations."),
      api: "useGetSlaViolationQuery",
      link: "requests",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Request Code"), dataIndex: "requestCode", width: 200 },
        { title: trans("Requester (Customer)"), dataIndex: "requester", width: 200 , render: (value) => value?.name || "- - - - -" },
        { title: trans("Request Type"), dataIndex: "requestType", width: 200 },
        { title: trans("Priority"), dataIndex: "priority", width: 120 , render: (value) => <Tag color={value === "high" ? "red" : value === "medium" ? "orange" : "green"}>{value}</Tag> },
        { title: trans("Violation Type"), dataIndex: "violationType", width: 200 },
        { title: trans("Assigned Staff"), dataIndex: "assignedStaff", width: 200, render: (value) => value?.name || "- - - - -" },
        { title: trans("Delay Duration"), dataIndex: "delayHours", width: 200,   render: (value) => {
            if (!value && value !== 0) return "-";

            const totalMinutes = Math.round(parseFloat(value) * 60);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;

            return `${hours}h ${minutes}m`;
          },
        },
        { title: trans("Request Status"), dataIndex: "status", width: 200, render: (value) => <Tag color={value === "done" ? "green" : "red"}>{value}</Tag> },
        { title: trans("Response Time"), dataIndex: "firstResponseAt", width: 200, render: (value) => formatDateString(value) },
        { title: trans("Resolution Time"), dataIndex: "resolutionAt", width: 200 , render: (value) => formatDateString(value) },
      ],
      query: {
        page: 1,
        limit: 10,
        from: dayjs().startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        to: dayjs().endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      },
      extractTotal: (response) => response?.data?.pagination?.total || 0,
    },
    {
      key: "idleTechnicians",
      title: trans("Idle Technicians"),
      icon: <UserOutlined />,
      color: colorScheme.medium,
      severity: "Medium",
      tooltip: trans("Inactive field staff."),
      link: "setup/technicians?filterBy=idle",
      api: "useGetAbsentIdleStaffQuery",
      extractData: (response) => response?.data?.technicians || [],
      frontendFilter: null,
      columns: [
        {
          title: trans("staff name"),
          dataIndex: "technicianName",
          width: 200,
        },
        {
          title: trans("Last Activity Time"),
          dataIndex: "timeElapsedFormatted",
          width: 180,
          render: (value) => value || "- - - - -",
        },
        {
          title: trans("Phone Number"),
          dataIndex: "phone",
          width: 150,
          render: (value) => value || "- - - - -",
        },
        {
          title: trans("Supervisor"),
          dataIndex: "supervisor",
          width: 120,
          render: (value) => value || "- - - - -",
        },
        {
          title: trans("department"),
          dataIndex: "department",
          width: 120,
          render: (value) => value || "- - - - -",
        },
      ],
      query: {
        page: 1,
        limit: 10,
        filterBy: "idle",
      },
      extractTotal: (response) => response?.data?.totalResults || 0,
    },
    {
      key: "absentStaff",
      title: trans("Absent Staff"),
      icon: <ThunderboltOutlined />,
      color: colorScheme.high,
      severity: "High",
      tooltip: trans("Currently absent staff."),
      api: "useGetAbsentIdleStaffQuery",
      link: "setup/technicians?filterBy=absent",
      extractData: (response) => response?.data?.technicians || [],
      frontendFilter: null,
      columns: [
        { title: trans("staff name"), dataIndex: "technicianName", width: 200 },
        { title: trans("Shift"), dataIndex: "assignedShift", width: 220 },
        { title: trans("Phone Number"), dataIndex: "phone", width: 150 },
        {
          title: trans("Supervisor"),
          dataIndex: "supervisor",
          width: 120,
          render: (value) => value || "- - - - -",
        },
        {
          title: trans("department"),
          dataIndex: "department",
          width: 120,
          render: (value) => value || "- - - - -",
        },
      ],
      query: {
        page: 1,
        limit: 10,
        filterBy: "absent",
      },
      extractTotal: (response) => response?.data?.totalResults || 0,
    },
    {
      key: "pendingForms",
      title: trans("Pending Forms"),
      icon: <FileTextOutlined />,
      color: colorScheme.low,
      severity: "Low",
      tooltip: trans("Forms awaiting submission or approval."),
      api: "useGetFormMetricsQuery",
      link: "tasks/tasks-board?filterBy=pending",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Form Name"), dataIndex: "formName", width: 200 },
        {
          title: trans("Created By"),
          dataIndex: "createdByName",
          width: 200,
          render: (value) => value || "- - - - -",
        },
        { title: trans("Date"), dataIndex: "taskStartDate", width: 150 ,render: (value) => value ? dayjs(value).format("DD-MM-YYYY") : "- - - - -"},
        {
          title: trans("Assigned Field Staff"),
          dataIndex: "assignedToName",
          width: 200,
          render: (value) => value || "- - - - -",
        },
        { title: trans("Task"), dataIndex: "taskName", width: 150 },
        { title: trans("POI"), dataIndex: "poiName", width: 180, render: (value) => value || "- - - - -", },
      ],
      query: {
        page: 1,
        limit: 10,
        filterBy: "pending",
        from: dayjs().startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        to: dayjs().endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      },
      extractTotal: (response) => response?.totalResults || 0,
    },
    {
      key: "unassignedRequests",
      title: trans("requests"),
      icon: <WarningOutlined />,
      color: colorScheme.medium,
      severity: "Medium",
      tooltip: trans("Requests under review."),
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      api: "useGetAllRequestsQuery",
      link: "requests?status=pending",
      columns: [
        { title: trans("Service Type"), dataIndex: "serviceType", width: 120 },
        {
          title: trans("Requester (Customer)"),
          dataIndex: "user",
          width: 180,
          render: (value) => value.name || "- - - - -",
        },
        {
          title: trans("Preferred Date"),
          dataIndex: "preferredTime",
          width: 200,
          render: (_, record) => (
            <span>
              {record?.preferredTime
                ? formatDateString(record?.preferredTime)
                : "- - - - -"}
            </span>
          ),
        },
        {
          title: trans("Created At"),
          dataIndex: "createdAt",
          width: 200,
          render: (value) => (
            <span>{value ? formatDateString(value) : "- - - - -"}</span>
          ),
        },
      ],
      query: {
        page: 1,
        limit: 10,
        queryString: "&status=pending",
      },
      extractTotal: (response) => response?.total || 0,
    },
  ];

  const visibleAlerts = canViewServiceRequest
    ? alertTypes
    : alertTypes.filter((a) => a.key !== "slaViolations" && a.key !== "unassignedRequests");

  const colSpan = Math.floor(24 / visibleAlerts.length);

  return (
    <Row gutter={[8, 8]}>
      {visibleAlerts.map((alert) => {
        const alertData = alerts?.[alert.key];
        const count = alertData?.count || 0;
        const change = alertData?.change || 0;

        return (
          <Col xs={24} sm={12} md={colSpan} key={alert.key}>
            <Tooltip title={trans(alert.tooltip)}>
              <div
                className="bg-white-rounded p-12 my-3 cursor"
                style={{
                  borderRadius: "12px",
                  padding: "10px 14px",
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  height: "100px",
                }}
                onClick={() => setSelectedAlert(alert)}
              >
                <div
                  style={{
                    display: "flex",
                    marginTop: 8,
                    justifyContent: "space-between",
                  }}
                >
                  {/* Title */}
                  <div>
                    <p
                      className="fs-12 fw-600 neutral-500"
                      style={{ color: "#42526E" }}
                    >
                      {alert.title}
                    </p>
                  </div>
                  <div>
                    <Tag
                      color={getSeverityColor(alert.severity.toLowerCase())}
                      style={{ fontWeight: 600, fontSize: 12 }}
                    >
                      {trans(alert.severity)}
                    </Tag>
                  </div>
                </div>

                {/* Count + change */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: 8,
                    justifyContent: "start",
                  }}
                >
                  <span
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      marginRight: 8,
                      color: "#111827",
                    }}
                  >
                    {count}
                  </span>
                  {change !== 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 4,
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={
                          change > 0
                            ? `${process.env.PUBLIC_URL}/assets/incbad.svg`
                            : `${process.env.PUBLIC_URL}/assets/decgood.svg`
                        }
                        alt=""
                      />
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 500,
                          color:
                            change > 0
                              ? colorScheme.danger
                              : colorScheme.success,
                        }}
                      >
                        {change > 0 ? `+${change}%` : `${change}%`}
                      </span>
                      <p
                        style={{
                          fontSize: 10,
                          fontWeight: 400,
                          color:
                            change > 0
                              ? colorScheme.danger
                              : colorScheme.success,
                          marginBottom: 0,
                        }}
                      >
                        {change > 0
                          ? trans("vs yesterday")
                          : trans("vs yesterday")}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Tooltip>
          </Col>
        );
      })}
      {selectedAlert && (
        <AlertPopup
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
        />
      )}
    </Row>
  );
}
